// Period presets -> {start, end} ISO date strings (yyyy-mm-dd)
export function iso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Parse an ISO date string (yyyy-mm-dd) into a LOCAL Date at midnight to avoid
// timezone drift (new Date('2026-07-04') would be parsed as UTC).
export function parseLocal(s) {
  if (!s) return null;
  const parts = String(s).slice(0, 10).split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday=0
  x.setDate(x.getDate() - day);
  return x;
}

export const PERIOD_PRESETS = [
  { key: 'today', label: "Aujourd'hui" },
  { key: 'yesterday', label: 'Hier' },
  { key: 'this_week', label: 'Cette semaine' },
  { key: 'last_week', label: 'Semaine préc.' },
  { key: 'this_month', label: 'Ce mois' },
  { key: 'last_month', label: 'Mois préc.' },
  { key: 'quarter', label: 'Trimestre' },
  { key: 'year', label: 'Année' },
  { key: 'all', label: 'Toute la période' },
];

// Short French month labels for readable comparison ranges.
const MONTHS_FR_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

function fmtDayFr(d) {
  return `${d.getDate()} ${MONTHS_FR_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

// --- Calendar math helpers (day is clamped to the target month length) ---
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function lastDayOfMonth(y, mIndex) {
  return new Date(y, mIndex + 1, 0).getDate();
}
function addMonthsClamped(d, n) {
  const mIndex = d.getMonth() + n;
  const targetY = d.getFullYear() + Math.floor(mIndex / 12);
  const targetM = ((mIndex % 12) + 12) % 12;
  const day = Math.min(d.getDate(), lastDayOfMonth(targetY, targetM));
  return new Date(targetY, targetM, day);
}
function addYearsClamped(d, n) {
  const targetY = d.getFullYear() + n;
  const m = d.getMonth();
  const day = Math.min(d.getDate(), lastDayOfMonth(targetY, m));
  return new Date(targetY, m, day);
}

// Compute the CALENDAR-ALIGNED comparison window for a given preset.
// - Period-to-date presets compare to the equivalent window of the previous
//   cycle (WoW / MoM / YoY), so "Ce mois" 1→16 juil. compares to 1→16 juin.
// - Complete blocks (Semaine préc., Mois préc.) compare to the full previous block.
// - Custom ranges compare to the equal-length window immediately preceding.
// - "Toute la période" has no meaningful comparison.
// Returns { start, end } as ISO strings (or nulls when no comparison applies).
export function computeComparison(periodKey, period) {
  const none = { start: null, end: null };
  if (!period || !period.start || !period.end) return none;
  if (periodKey === 'all') return none;
  const s = parseLocal(period.start);
  const e = parseLocal(period.end);
  if (!s || !e) return none;

  let ps, pe;
  switch (periodKey) {
    case 'today':
    case 'yesterday': {
      const len = Math.round((e - s) / 86400000) + 1;
      ps = addDays(s, -len); pe = addDays(e, -len); break;
    }
    case 'this_week':
      // Same weekdays of the previous week (shift both bounds by 7 days).
      ps = addDays(s, -7); pe = addDays(e, -7); break;
    case 'last_week':
      // Full previous week (already a complete Mon→Sun block).
      ps = addDays(s, -7); pe = addDays(e, -7); break;
    case 'this_month':
      // Same day-of-month range of the previous month (clamped).
      ps = addMonthsClamped(s, -1); pe = addMonthsClamped(e, -1); break;
    case 'last_month': {
      // Full month immediately before the selected (complete) month.
      ps = new Date(s.getFullYear(), s.getMonth() - 1, 1);
      pe = new Date(s.getFullYear(), s.getMonth(), 0);
      break;
    }
    case 'quarter': {
      // Selection is now a full quarter → compare to the previous full quarter.
      ps = new Date(s.getFullYear(), s.getMonth() - 3, 1);
      pe = new Date(s.getFullYear(), s.getMonth(), 0);
      break;
    }
    case 'year':
      // Year-over-year to date (clamped for leap days).
      ps = addYearsClamped(s, -1); pe = addYearsClamped(e, -1); break;
    case 'custom':
    default: {
      const len = Math.round((e - s) / 86400000) + 1;
      pe = addDays(s, -1); ps = addDays(pe, -(len - 1)); break;
    }
  }
  return { start: iso(ps), end: iso(pe) };
}

// Describe the comparison window used for KPI trends (+/- %). Turns the
// calendar-aligned comparison into explicit, human-readable labels so users
// understand exactly what the trend refers to. `compare` may be provided to
// guarantee the label matches the exact window sent to the backend.
export function comparisonInfo(periodKey, period, compare) {
  const cs = period ? parseLocal(period.start) : null;
  const ce = period ? parseLocal(period.end) : null;
  const curRange = (cs && ce)
    ? (period.start === period.end ? fmtDayFr(cs) : `${fmtDayFr(cs)} → ${fmtDayFr(ce)}`)
    : null;

  const noPrev = {
    hasPrev: false,
    short: 'aucune comparaison',
    range: null,
    curRange,
    full: "Aucune comparaison sur « Toute la période ». Sélectionnez une période délimitée : les tendances (▲/▼) comparent alors la période choisie à la période équivalente précédente.",
  };
  if (periodKey === 'all') return noPrev;
  const cmp = (compare && compare.start && compare.end) ? compare : computeComparison(periodKey, period);
  if (!cmp || !cmp.start || !cmp.end) return noPrev;
  const ps = parseLocal(cmp.start);
  const pe = parseLocal(cmp.end);
  if (!ps || !pe) return noPrev;

  const sameDay = cmp.start === cmp.end;
  const range = sameDay ? fmtDayFr(ps) : `${fmtDayFr(ps)} → ${fmtDayFr(pe)}`;
  const length = Math.round((pe - ps) / 86400000) + 1;

  const LABELS = {
    today: { short: 'vs hier', intro: 'Comparé à hier' },
    yesterday: { short: 'vs avant-hier', intro: 'Comparé à avant-hier' },
    this_week: { short: 'vs semaine préc.', intro: 'Comparé aux mêmes jours de la semaine précédente' },
    last_week: { short: 'vs semaine préc.', intro: 'Comparé à la semaine complète précédente' },
    this_month: { short: 'vs mois préc.', intro: 'Comparé à la même période du mois précédent' },
    last_month: { short: 'vs mois préc.', intro: 'Comparé au mois complet précédent' },
    quarter: { short: 'vs trim. préc.', intro: 'Comparé au trimestre complet précédent' },
    year: { short: 'vs an préc. (YoY)', intro: "Comparé à la même période de l'année précédente" },
  };
  const fallback = {
    short: sameDay ? 'vs la veille' : `vs ${length} j. préc.`,
    intro: `Comparé à la période précédente de même durée (${length === 1 ? '1 jour' : `${length} jours`})`,
  };
  const l = LABELS[periodKey] || fallback;
  const full = `Période analysée : ${curRange || '—'}. ${l.intro} : ${range}.`;
  return { hasPrev: true, short: l.short, range, curRange, length, full };
}

export function computePeriod(key, ref, dataMin, dataMax) {
  // Relative presets ("today", "this week"…) are anchored to the REAL system clock
  // so they reflect live reporting. An explicit `ref` can override for testing.
  const today = ref ? parseLocal(ref) : new Date();
  let start, end;
  switch (key) {
    case 'today':
      start = new Date(today); end = new Date(today); break;
    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      start = new Date(y); end = new Date(y); break;
    }
    case 'this_week':
      start = startOfWeek(today); end = new Date(today); break;
    case 'last_week': {
      const s = startOfWeek(today); s.setDate(s.getDate() - 7);
      const e = new Date(s); e.setDate(e.getDate() + 6);
      start = s; end = e; break;
    }
    case 'this_month':
      start = new Date(today.getFullYear(), today.getMonth(), 1); end = new Date(today); break;
    case 'last_month':
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0); break;
    case 'quarter': {
      // Last COMPLETE calendar quarter — unambiguous and always meaningful
      // (avoids a tiny "quarter-to-date" right after a quarter starts).
      const cq = Math.floor(today.getMonth() / 3);       // current quarter 0..3
      const py = cq === 0 ? today.getFullYear() - 1 : today.getFullYear();
      const pq = (cq + 3) % 4;                            // previous quarter index
      start = new Date(py, pq * 3, 1);
      end = new Date(py, pq * 3 + 3, 0);                  // last day of that quarter
      break;
    }
    case 'year':
      // Year TO DATE (1 Jan → today) for a like-for-like YoY comparison.
      start = new Date(today.getFullYear(), 0, 1); end = new Date(today); break;
    case 'all':
    default:
      return { start: dataMin || null, end: dataMax || null };
  }
  return { start: iso(start), end: iso(end) };
}
